// German for the contracting page: the verdict, the readiness ladder, the document, the payment
// schedule, change requests, the record (baselines, diff, signature, clause library) and every
// modal. Registered on load. Keys are the English text as written in ContractingPage.tsx.
import { registerDe } from '../i18n/index.js';

registerDe({
  // ── small shared bits ──────────────────────────────────────────────────────
  'Understood': 'Verstanden',
  'Nothing else points at it — the row goes on its own.': 'Nichts anderes verweist darauf — die Zeile geht allein.',
  'This also removes:': 'Dies entfernt auch:',
  'Cancel': 'Abbrechen',
  'Save': 'Speichern',
  'Edit': 'Bearbeiten',
  'Add': 'Hinzufügen',
  'New': 'Neu',
  'Delete': 'Löschen',
  'Title': 'Titel',
  'Status': 'Status',
  'of': 'von',
  'on': 'am',
  'or': 'oder',
  'from': 'aus',
  'due': 'fällig',
  'no date': 'ohne Datum',
  'd': 'T',
  'rev': 'Rev.',
  'ID': 'ID',
  'Hash': 'Hash',
  'Ref': 'Ref',
  'Detail': 'Detail',
  'Change': 'Änderung',
  'Total': 'Total',

  // ── status, priority and type tokens as displayed ──────────────────────────
  'draft': 'Entwurf',
  'issued': 'ausgestellt',
  'signed': 'unterzeichnet',
  'superseded': 'ersetzt',
  'submitted': 'eingereicht',
  'approved': 'genehmigt',
  'rejected': 'abgelehnt',
  'deferred': 'zurückgestellt',
  'withdrawn': 'zurückgezogen',
  'planned': 'geplant',
  'in progress': 'in Arbeit',
  'delivered': 'geliefert',
  'accepted': 'abgenommen',
  'invoiced': 'fakturiert',
  'confirmed': 'bestätigt',
  'baselined': 'in der Baseline',
  'descoped': 'aus dem Umfang genommen',
  'dropped': 'verworfen',
  'must': 'Muss',
  'should': 'Soll',
  'could': 'Kann',
  'wont': 'Nicht',
  'functional': 'funktional',
  'non functional': 'nicht-funktional',
  'constraint': 'Rahmenbedingung',
  'assumption': 'Annahme',
  'out of scope': 'ausserhalb des Umfangs',
  'added': 'hinzugefügt',
  'changed': 'geändert',
  'removed': 'entfernt',
  'modify': 'ändern',
  'create': 'erstellen',
  'descope': 'aus dem Umfang nehmen',

  // ── toasts and error fallbacks ─────────────────────────────────────────────
  'The pre-flight did not answer.': 'Die Vorprüfung hat nicht geantwortet.',
  'Failed to load contracting metadata': 'Vertragsmetadaten konnten nicht geladen werden',
  'Failed to load the clause library': 'Die Klauselbibliothek konnte nicht geladen werden',
  'Failed to load the SOW version': 'Die SOW-Version konnte nicht geladen werden',
  'Re-rendered from the register · {hash}': 'Aus dem Register neu gerendert · {hash}',
  'Could not save the draft': 'Der Entwurf konnte nicht gespeichert werden',
  'Version {n} rendered from the live register': 'Version {n} aus dem Live-Register gerendert',
  'Could not create the version': 'Die Version konnte nicht erstellt werden',
  'Could not create the SOW': 'Die SOW konnte nicht erstellt werden',
  'Could not check the version before issuing': 'Die Version konnte vor dem Ausstellen nicht geprüft werden',
  'Issued.': 'Ausgestellt.',
  'The register had moved — the issued document reflects today.': 'Das Register hatte sich bewegt — das ausgestellte Dokument entspricht dem heutigen Stand.',
  'Could not issue the version': 'Die Version konnte nicht ausgestellt werden',
  'Signature recorded.': 'Unterschrift erfasst.',
  'Could not record the signature': 'Die Unterschrift konnte nicht erfasst werden',
  '{key} staged — it goes in when the document is created': '{key} vorgemerkt — wird beim Erstellen des Dokuments übernommen',
  '{key} written · re-rendered {hash}': '{key} geschrieben · neu gerendert {hash}',
  'Could not write the value': 'Der Wert konnte nicht geschrieben werden',
  'Could not work out what deleting this would do': 'Es liess sich nicht ermitteln, was das Löschen bewirken würde',
  '{name} deleted': '{name} gelöscht',
  'Could not delete': 'Löschen nicht möglich',
  'Milestone added': 'Meilenstein hinzugefügt',
  'Could not add the milestone': 'Der Meilenstein konnte nicht hinzugefügt werden',
  'Milestone updated': 'Meilenstein aktualisiert',
  'Could not update the milestone': 'Der Meilenstein konnte nicht aktualisiert werden',
  'Could not reorder': 'Die Reihenfolge konnte nicht geändert werden',
  'Could not load the change request': 'Der Änderungsantrag konnte nicht geladen werden',
  '{ref} impact updated': 'Auswirkung von {ref} aktualisiert',
  'Could not save the impact': 'Die Auswirkung konnte nicht gespeichert werden',
  '{ref} submitted to the client — uncosted, on your confirmation': '{ref} beim Kunden eingereicht — unbeziffert, auf Ihre Bestätigung',
  '{ref} submitted to the client': '{ref} beim Kunden eingereicht',
  'Could not submit {ref}': '{ref} konnte nicht eingereicht werden',
  '{ref} created as a draft': '{ref} als Entwurf erstellt',
  'Could not create the change request': 'Der Änderungsantrag konnte nicht erstellt werden',
  '{ref} → {status}': '{ref} → {status}',
  'Could not move {ref} to {status}': '{ref} konnte nicht auf {status} gesetzt werden',
  'Could not load the register': 'Das Register konnte nicht geladen werden',
  '{ref} approved · {n} revision(s) applied, addendum v{v} drafted': '{ref} genehmigt · {n} Revision(en) angewendet, Nachtrag v{v} entworfen',
  '{ref} approved · {n} revision(s) applied': '{ref} genehmigt · {n} Revision(en) angewendet',
  'Could not approve the change request': 'Der Änderungsantrag konnte nicht genehmigt werden',
  'Could not compute the diff': 'Der Vergleich konnte nicht berechnet werden',
  'Clause {key} v{v} created': 'Klausel {key} v{v} erstellt',
  '{key} is now v{v} — signed SOWs keep the version they printed': '{key} ist jetzt v{v} — unterzeichnete SOWs behalten die gedruckte Version',
  'Could not save the clause': 'Die Klausel konnte nicht gespeichert werden',
  '{key} retired': '{key} stillgelegt',
  '{key} restored': '{key} wiederhergestellt',
  'Could not change the clause': 'Die Klausel konnte nicht geändert werden',

  // ── the readiness ladder ───────────────────────────────────────────────────
  'Register →': 'Register →',
  'Schedule ↓': 'Zahlungsplan ↓',
  'Fill ↓': 'Ausfüllen ↓',
  'the register speaking': 'das Register spricht',
  'the generator speaking': 'der Generator spricht',
  'Linter:': 'Linter:',
  'Fails the acceptance-criterion linter.': 'Besteht den Abnahmekriterien-Linter nicht.',
  'Go to the payment schedule ↓': 'Zum Zahlungsplan ↓',
  'unverifiable ACs': 'nicht prüfbare AK',
  'acceptance criterion is not verifiable as written': 'Abnahmekriterium ist so, wie es geschrieben ist, nicht prüfbar',
  'acceptance criteria are not verifiable as written': 'Abnahmekriterien sind so, wie sie geschrieben sind, nicht prüfbar',
  'Issuing puts Annex A into the contract. A criterion the linter rejects cannot be proved met by anybody — not the client at UAT, not you at acceptance. The server refuses to issue until these are fixed, or until you say out loud that they go in as written.':
    'Das Ausstellen bringt Anhang A in den Vertrag. Ein Kriterium, das der Linter ablehnt, kann von niemandem als erfüllt nachgewiesen werden — nicht vom Kunden im UAT, nicht von Ihnen bei der Abnahme. Der Server verweigert das Ausstellen, bis diese korrigiert sind oder bis Sie ausdrücklich sagen, dass sie so, wie sie geschrieben sind, hineingehen.',
  'Open each criterion in the register and add the number, count or duration a tester checks. Then re-save the draft.':
    'Öffnen Sie jedes Kriterium im Register und ergänzen Sie die Zahl, Anzahl oder Dauer, die ein Tester prüft. Speichern Sie den Entwurf dann erneut.',
  'Open {ref} in the Register →': '{ref} im Register öffnen →',
  'the criterion': 'das Kriterium',
  'no exclusions': 'keine Ausschlüsse',
  'Nothing is expressly out of scope': 'Nichts ist ausdrücklich ausserhalb des Umfangs',
  'The contract prints an': 'Was auch geschieht, der Vertrag druckt den Abschnitt',
  'Out of scope': 'Ausserhalb des Umfangs',
  'section whatever happens. With no requirement typed': '— immer. Ohne eine Anforderung vom Typ',
  'and none descoped, that section reads': 'und ohne etwas aus dem Umfang Genommenes lautet dieser Abschnitt',
  '— the one page a scope dispute is decided on, blank. Exclusions are first-class in the schema precisely so they can be printed.':
    '— die eine Seite, auf der ein Streit über den Umfang entschieden wird, bleibt leer. Ausschlüsse sind im Schema gerade deshalb erstklassig, damit sie gedruckt werden können.',
  'What the section will print': 'Was der Abschnitt drucken wird',
  'Type the things you discussed and are NOT doing as requirements of type': 'Erfassen Sie, was Sie besprochen haben und NICHT tun, als Anforderungen vom Typ',
  'They print as the exclusions list.': 'Sie werden als Ausschlussliste gedruckt.',
  'New out-of-scope requirement →': 'Neue Anforderung ausserhalb des Umfangs →',
  'Assumptions': 'Annahmen',
  'Constraints': 'Rahmenbedingungen',
  'empty sections': 'leere Abschnitte',
  'priced-on section will print empty — {names}': 'preisrelevanter Abschnitt wird leer gedruckt — {names}',
  'priced-on sections will print empty — {names}': 'preisrelevante Abschnitte werden leer gedruckt — {names}',
  'These sections tell the client what the price assumed and what bound the solution. Empty, they hand back every assumption you actually made. They are populated by typing requirements as':
    'Diese Abschnitte sagen dem Kunden, was der Preis voraussetzte und was die Lösung eingrenzte. Leer geben sie jede Annahme zurück, die Sie tatsächlich getroffen haben. Sie werden gefüllt, indem Anforderungen im Register als',
  'in the register — no separate form exists, and none should.': 'erfasst werden — ein separates Formular gibt es nicht, und es sollte keines geben.',
  'Register composition': 'Zusammensetzung des Registers',
  'no requirements': 'keine Anforderungen',
  'counted, not judged': 'gezählt, nicht beurteilt',
  'Same surface, different type:': 'Gleiche Oberfläche, anderer Typ:',
  'for what the price assumed,': 'für das, was der Preis voraussetzte,',
  'for what was fixed.': 'für das, was festgelegt war.',
  'Open the register →': 'Register öffnen →',
  'unfunded milestone': 'Meilenstein ohne Arbeitsergebnis',
  'payment milestone carries money but no deliverable': 'Zahlungsmeilenstein trägt Geld, aber kein Arbeitsergebnis',
  'payment milestones carry money but no deliverable': 'Zahlungsmeilensteine tragen Geld, aber kein Arbeitsergebnis',
  'An acceptance record is issued': 'Ein Abnahmeprotokoll wird',
  'for a milestone': 'für einen Meilenstein',
  'and lists the deliverables it accepts. A milestone with an amount and no deliverable is an invoice trigger with nothing to trigger it — the client is asked to pay on the acceptance of nothing.':
    'ausgestellt und listet die Arbeitsergebnisse auf, die es abnimmt. Ein Meilenstein mit Betrag und ohne Arbeitsergebnis ist ein Rechnungsauslöser ohne Auslöser — der Kunde wird gebeten, für die Abnahme von nichts zu zahlen.',
  'visible to the client': 'für den Kunden sichtbar',
  'deliverables': 'Arbeitsergebnisse',
  'acceptance records': 'Abnahmeprotokolle',
  'counted': 'gezählt',
  'Either attach a deliverable to the milestone, or fold its amount into a milestone that has one. A payment needs something to accept.':
    'Hängen Sie entweder ein Arbeitsergebnis an den Meilenstein oder schlagen Sie seinen Betrag einem Meilenstein zu, der eines hat. Eine Zahlung braucht etwas, das abgenommen wird.',
  'blank variables': 'leere Variablen',
  'contract variable the record can fill': 'Vertragsvariable, die die Akte füllen kann',
  'contract variables the record can fill': 'Vertragsvariablen, die die Akte füllen kann',
  '{n} only you can': '{n} nur Sie',
  'A new version renders with every variable outside the defaults blank. These values are already columns in this database; retyping them is how a signature block ends up naming a company where it should name a person.':
    'Eine neue Version wird mit jeder Variablen ausserhalb der Standardwerte leer gerendert. Diese Werte sind bereits Spalten in dieser Datenbank; sie erneut einzutippen ist der Weg, auf dem ein Unterschriftsblock am Ende eine Firma nennt, wo er eine Person nennen sollte.',
  'staged': 'vorgemerkt',
  'in the document': 'im Dokument',
  'v{n} is frozen': 'v{n} ist eingefroren',
  'Use': 'Übernehmen',
  'No column holds these — you type them:': 'Keine Spalte enthält diese — Sie tippen sie ein:',
  'Each value above names the column it came from. Accepting one copies it into': 'Jeder Wert oben nennt die Spalte, aus der er stammt. Wird er übernommen, wird er kopiert nach',
  '— nothing is inferred and nothing is typed twice.': '— nichts wird abgeleitet und nichts zweimal getippt.',
  'value mismatch': 'Wertabweichung',
  'Milestone total {total} matches the engagement estimate': 'Meilensteinsumme {total} entspricht der Auftragsschätzung',
  'Milestone total {total} does not match the engagement estimate {estimate}': 'Meilensteinsumme {total} entspricht nicht der Auftragsschätzung {estimate}',
  'The figure the SOW prints and the figure gate G2 reads are the same number.': 'Die Zahl, die die SOW druckt, und die Zahl, die Gate G2 liest, sind dieselbe.',
  'G2 reads whichever is present; the SOW prints the milestone sum. Two different numbers describe this engagement and the contract will print one of them without saying so.':
    'G2 liest, was vorhanden ist; die SOW druckt die Meilensteinsumme. Zwei verschiedene Zahlen beschreiben diesen Auftrag, und der Vertrag druckt eine davon, ohne es zu sagen.',
  'Both figures': 'Beide Zahlen',
  'milestones sum': 'Meilensteinsumme',
  'added, not judged': 'addiert, nicht beurteilt',
  'Decide which number is true and make the other match. The contract prints the milestone sum; G2 reads whichever it finds.':
    'Entscheiden Sie, welche Zahl stimmt, und gleichen Sie die andere an. Der Vertrag druckt die Meilensteinsumme; G2 liest, was es findet.',
  'draft requirements': 'Anforderungen im Entwurf',
  'of {total} requirements are still draft': 'von {total} Anforderungen sind noch Entwurf',
  '{total} of {total} requirements held': '{total} von {total} Anforderungen übernommen',
  'A draft requirement prints into the scope table exactly like a held one. G1 has not been cleared for these.':
    'Eine Anforderung im Entwurf wird in der Umfangstabelle genau wie eine übernommene gedruckt. G1 ist für diese nicht freigegeben.',
  'Every row the scope section will print has been committed by a human.': 'Jede Zeile, die der Umfangsabschnitt drucken wird, wurde von einem Menschen bestätigt.',
  'Scope table, as it will print': 'Umfangstabelle, wie sie gedruckt wird',
  'Confirm them, or drop them. A draft row prints into the scope table looking exactly like a committed one.':
    'Bestätigen Sie sie oder verwerfen Sie sie. Eine Entwurfszeile sieht in der Umfangstabelle genau wie eine bestätigte aus.',
  'Open the register, filtered to draft →': 'Register öffnen, gefiltert nach Entwurf →',

  // ── the rail ───────────────────────────────────────────────────────────────
  'DOCUMENT': 'DOKUMENT',
  'WOULD PRINT': 'WÜRDE DRUCKEN',
  'VALUE': 'WERT',
  'NOT FIT TO SIGN': 'NICHT UNTERSCHRIFTSREIF',
  'G3 · SOW SIGNED': 'G3 · SOW UNTERZEICHNET',
  'no SOW exists': 'keine SOW vorhanden',
  '{n} in scope': '{n} im Umfang',
  '{must} must · {excluded} excluded': '{must} Muss · {excluded} ausgeschlossen',
  'the pre-flight did not answer': 'die Vorprüfung hat nicht geantwortet',
  'no estimate on record': 'keine Schätzung erfasst',
  '= engagement estimate': '= Auftragsschätzung',
  'estimate {estimate}': 'Schätzung {estimate}',
  'nothing outstanding': 'nichts offen',
  '{met} of {total}': '{met} von {total}',
  '{autoMet}/{autoTotal} auto · {manMet}/{manTotal} manual': '{autoMet}/{autoTotal} automatisch · {manMet}/{manTotal} manuell',
  'gate engine did not answer': 'Gate-Engine hat nicht geantwortet',
  'document': 'Dokument',
  'register': 'Register',
  'schedule': 'Zeitplan',
  'the list': 'die Liste',
  'lifecycle': 'Lebenszyklus',
  'Open the {target}': '{target} öffnen',

  // ── the pipeline and the section map ───────────────────────────────────────
  'Register': 'Register',
  'Draft': 'Entwurf',
  'Issued': 'Ausgestellt',
  'Signed': 'Unterzeichnet',
  'Baseline': 'Baseline',
  'not created': 'nicht erstellt',
  'no contact nominated': 'kein Kontakt benannt',
  'set': 'gesetzt',
  'no background recorded': 'kein Hintergrund erfasst',
  '{count} requirements · {groups}': '{count} Anforderungen · {groups}',
  '{n} group': '{n} Gruppe',
  '{n} groups': '{n} Gruppen',
  '{n} excluded': '{n} ausgeschlossen',
  '{n} deliverable': '{n} Arbeitsergebnis',
  '{n} deliverables': '{n} Arbeitsergebnisse',
  '{n} milestone': '{n} Meilenstein',
  '{n} milestones': '{n} Meilensteine',
  '{n} working days': '{n} Arbeitstage',
  '{n} criteria · {bad} unverifiable': '{n} Kriterien · {bad} nicht prüfbar',
  '{n} criteria': '{n} Kriterien',
  'named': 'benannt',
  'client name in place of a person': 'Kundenname anstelle einer Person',

  // ── masthead and verdict ───────────────────────────────────────────────────
  'Contracting': 'Vertrag',
  'No engagement selected': 'Kein Auftrag ausgewählt',
  'Pick an engagement in the top bar — a Statement of Work is always scoped to one.': 'Wählen Sie in der Kopfleiste einen Auftrag — eine Leistungsbeschreibung (SOW) gehört immer zu genau einem.',
  'contracting as {name}': 'Vertragspartei {name}',
  'Reading the register…': 'Register wird gelesen…',
  'The pre-flight could not be computed, so this page cannot say whether the register is fit to print a contract.':
    'Die Vorprüfung konnte nicht berechnet werden; diese Seite kann daher nicht sagen, ob das Register reif ist, einen Vertrag zu drucken.',
  'The register is not yet fit to print a contract —': 'Das Register ist noch nicht reif, einen Vertrag zu drucken —',
  'hard stop,': 'harter Stopp,',
  'hard stops,': 'harte Stopps,',
  'thing worth fixing first.': 'Punkt, den Sie vorher beheben sollten.',
  'things worth fixing first.': 'Punkte, die Sie vorher beheben sollten.',
  'You can create the document now; the server will refuse to': 'Sie können das Dokument jetzt erstellen; der Server verweigert das',
  'issue': 'Ausstellen',
  'it while the hard stops stand.': 'jedoch, solange die harten Stopps bestehen.',
  'No Statement of Work exists. The register would print': 'Es existiert keine Leistungsbeschreibung (SOW). Das Register würde',
  'requirements into one.': 'Anforderungen in eine drucken.',
  'is a draft. Nothing has left the building.': 'ist ein Entwurf. Nichts hat das Haus verlassen.',
  'Issuing re-renders without the draft marker, publishes to the portal and writes the PDF.': 'Das Ausstellen rendert ohne Entwurfsmarkierung neu, veröffentlicht im Portal und schreibt das PDF.',
  'is with the client, issued {when}. It is the hash they will sign:': 'liegt beim Kunden, ausgestellt am {when}. Dies ist der Hash, den er unterzeichnen wird:',
  'Recording the signature freezes the register as an immutable baseline. After that, scope moves only through a change request.':
    'Das Erfassen der Unterschrift friert das Register als unveränderliche Baseline ein. Danach bewegt sich der Umfang nur noch über einen Änderungsantrag.',
  'by {name}': 'von {name}',
  'holds': 'umfasst',
  '{n} requirement': '{n} Anforderung',
  '{n} requirements': '{n} Anforderungen',
  'G3 is cleared on the record. The only route through the baseline is a change request.': 'G3 ist aktenkundig freigegeben. Der einzige Weg durch die Baseline ist ein Änderungsantrag.',
  'Create the SOW': 'SOW erstellen',
  'Save the draft first — the preview is behind your edits': 'Speichern Sie zuerst den Entwurf — die Vorschau hinkt Ihren Änderungen hinterher',
  'Issue to the client': 'An den Kunden ausstellen',
  'Record the signature': 'Unterschrift erfassen',
  'Raise a change request': 'Änderungsantrag stellen',

  // ── zone 1 · fit to sign ───────────────────────────────────────────────────
  'Fit to sign': 'Unterschriftsreif',
  'Fit to sign — v{n} draft': 'Unterschriftsreif — v{n} Entwurf',
  'Fit to sign — what a v{n} would carry': 'Unterschriftsreif — was eine v{n} enthalten würde',
  'Fit to sign — v{n} is frozen; this is what an addendum would carry': 'Unterschriftsreif — v{n} ist eingefroren; das würde ein Nachtrag enthalten',
  'every row states its own evidence and routes to the surface that owns the fix': 'jede Zeile nennt ihren eigenen Beleg und führt zur Oberfläche, der die Behebung gehört',
  'The gate tallies': 'Die Gate-Zählungen',
  'The pre-flight did not answer': 'Die Vorprüfung hat nicht geantwortet',
  'GET /api/contracting/readiness failed.': 'GET /api/contracting/readiness ist fehlgeschlagen.',
  'blocks issuing — the server refuses': 'blockiert das Ausstellen — der Server verweigert',
  'block issuing — the server refuses': 'blockieren das Ausstellen — der Server verweigert',
  'worth fixing — nothing stops you': 'zu beheben — nichts hält Sie auf',
  'met': 'erfüllt',
  'Why it is met': 'Warum erfüllt',
  'The fix': 'Die Behebung',
  'Nothing to do — this condition is satisfied by the rows on the left.': 'Nichts zu tun — diese Bedingung ist durch die Zeilen links erfüllt.',
  'conditions met': 'Bedingungen erfüllt',

  // ── zone 2 · the document ──────────────────────────────────────────────────
  'The document': 'Das Dokument',
  '{n} section override': '{n} Abschnittsüberschreibung',
  '{n} section overrides': '{n} Abschnittsüberschreibungen',
  'not created — this is what it would contain': 'nicht erstellt — das würde es enthalten',
  'Versions': 'Versionen',
  'addendum': 'Nachtrag',
  'Variables': 'Variablen',
  'have a value': 'haben einen Wert',
  'narrative': 'Narrativ',
  'commercial': 'Kommerziell',
  'parties': 'Parteien',
  '{set} of {total}': '{set} von {total}',
  'blank — the record offers a value in': 'leer — die Akte bietet einen Wert unter',
  'blank — the record offers a value ↑': 'leer — die Akte bietet einen Wert ↑',
  'blank — the signature block degrades without it': 'leer — der Unterschriftsblock verliert ohne diesen Wert',
  'Save & re-render': 'Speichern & neu rendern',
  'Unsaved edits — the preview still shows the last render.': 'Ungespeicherte Änderungen — die Vorschau zeigt noch das letzte Rendering.',
  'Clauses': 'Klauseln',
  '{n} pinned at the version this document printed': '{n} fixiert auf die Version, die dieses Dokument gedruckt hat',
  '{n} would print, at their latest version': '{n} würden gedruckt, in ihrer neuesten Version',
  'v{n} available': 'v{n} verfügbar',
  'Re-pin to latest': 'Auf neueste fixieren',
  'Edit the library →': 'Bibliothek bearbeiten →',
  'A signed SOW keeps the clause version it printed.': 'Eine unterzeichnete SOW behält die Klauselversion, die sie gedruckt hat.',
  'Sections': 'Abschnitte',
  'what each will print': 'was jeder drucken wird',
  'An override replaces the generated section entirely and is flagged on every regeneration. Clear the box to go back to the projection.':
    'Eine Überschreibung ersetzt den generierten Abschnitt vollständig und wird bei jeder Neugenerierung markiert. Leeren Sie das Feld, um zur Projektion zurückzukehren.',
  'overridden': 'überschrieben',
  'Leave empty to use the generated text.': 'Leer lassen, um den generierten Text zu verwenden.',
  'Document preview': 'Dokumentvorschau',
  'rendered from the register · this HTML is canonical · paper in both themes, because it prints': 'aus dem Register gerendert · dieses HTML ist massgeblich · Papier in beiden Designs, weil es gedruckt wird',
  'open HTML': 'HTML öffnen',
  'Rendered without the draft marker or the lint notes — what the client would receive': 'Ohne Entwurfsmarkierung und Linter-Hinweise gerendert — was der Kunde erhalten würde',
  'as the client sees it': 'wie der Kunde es sieht',
  'baseline': 'Baseline',
  'New version from register': 'Neue Version aus dem Register',
  'Delete this draft — it has never been issued': 'Diesen Entwurf löschen — er wurde nie ausgestellt',
  'Draft v{n}': 'Entwurf v{n}',
  'Discard draft': 'Entwurf verwerfen',
  'Not rendered yet.': 'Noch nicht gerendert.',
  'No document has been rendered yet. The section map on the left is what a first version would print, read from the live register — the same projection the generator uses. Creating the SOW renders it and nothing else moves.':
    'Es wurde noch kein Dokument gerendert. Die Abschnittsübersicht links zeigt, was eine erste Version drucken würde, gelesen aus dem Live-Register — dieselbe Projektion, die der Generator verwendet. Das Erstellen der SOW rendert sie, und sonst bewegt sich nichts.',

  // ── zone 3 · payment schedule ──────────────────────────────────────────────
  'Payment schedule': 'Zahlungsplan',
  'across': 'über',
  '— the figure the SOW prints and the figure G2 reads': '— die Zahl, die die SOW druckt, und die Zahl, die G2 liest',
  'The payment schedule did not load': 'Der Zahlungsplan wurde nicht geladen',
  'No milestones yet': 'Noch keine Meilensteine',
  'G2 needs at least one priced milestone as the estimate, and the SOW prints this table as its payment schedule.':
    'G2 braucht mindestens einen bepreisten Meilenstein als Schätzung, und die SOW druckt diese Tabelle als ihren Zahlungsplan.',
  'Add the first milestone': 'Ersten Meilenstein hinzufügen',
  'Milestone': 'Meilenstein',
  'Due': 'Fällig',
  'Amount': 'Betrag',
  'Accepts': 'Nimmt ab',
  'Client': 'Kunde',
  'Milestone name': 'Meilensteinname',
  'Due date': 'Fälligkeitsdatum',
  'visible': 'sichtbar',
  'hidden': 'verborgen',
  'Move up': 'Nach oben',
  'Move down': 'Nach unten',
  'deliverable': 'Arbeitsergebnis',
  'money, nothing to accept': 'Geld, nichts abzunehmen',
  'Delete milestone {name}': 'Meilenstein {name} löschen',
  'Delete this milestone — refused once it is in the signed record': 'Diesen Meilenstein löschen — verweigert, sobald er in der unterzeichneten Akte ist',
  'says': 'sagt',
  'New milestone name': 'Name des neuen Meilensteins',
  'New milestone due date': 'Fälligkeitsdatum des neuen Meilensteins',
  'amount': 'Betrag',
  'New milestone amount': 'Betrag des neuen Meilensteins',
  'Add a milestone': 'Meilenstein hinzufügen',

  // ── zone 4 · change requests ───────────────────────────────────────────────
  'Change requests': 'Änderungsanträge',
  'open': 'offen',
  'the only route through baseline': 'der einzige Weg durch Baseline',
  'no baseline yet — nothing is frozen, so these are still ordinary scope edits': 'noch keine Baseline — nichts ist eingefroren, das sind also noch gewöhnliche Umfangsänderungen',
  'New change request': 'Neuer Änderungsantrag',
  'No change requests': 'Keine Änderungsanträge',
  'The baseline is frozen and nothing has been asked for since. Every scope move from here raises one.': 'Die Baseline ist eingefroren und seither wurde nichts verlangt. Jede Umfangsänderung ab hier stellt einen.',
  'revised {refs}': '{refs} revidiert',
  'no charge': 'kostenlos',
  'uncosted': 'unbeziffert',
  'goodwill': 'Kulanz',

  // ── the record ─────────────────────────────────────────────────────────────
  'The record‖contract': 'Die Akte',
  '{n} baseline': '{n} Baseline',
  '{n} baselines': '{n} Baselines',
  '{n} SOW version': '{n} SOW-Version',
  '{n} SOW versions': '{n} SOW-Versionen',
  '{n} clause': '{n} Klausel',
  '{n} clauses': '{n} Klauseln',
  'in the library': 'in der Bibliothek',
  'Baselines': 'Baselines',
  'immutable snapshots — the event that freezes the register': 'unveränderliche Momentaufnahmen — das Ereignis, das das Register einfriert',
  'No baseline yet. One is created the moment a SOW version is signed — that is the event that freezes the register.':
    'Noch keine Baseline. Eine entsteht in dem Moment, in dem eine SOW-Version unterzeichnet wird — das ist das Ereignis, das das Register einfriert.',
  'Label': 'Bezeichnung',
  'Created': 'Erstellt',
  'Requirements': 'Anforderungen',
  'Deliverables': 'Arbeitsergebnisse',
  'Compare to live': 'Mit Live vergleichen',
  'What has moved': 'Was sich bewegt hat',
  '{n} record': '{n} Datensatz',
  '{n} records': '{n} Datensätze',
  'differ': 'unterscheiden sich',
  'have moved': 'haben sich bewegt',
  'between {from} and {to}': 'zwischen {from} und {to}',
  '{n} under {ref}': '{n} unter {ref}',
  'pick two points and compare': 'zwei Punkte wählen und vergleichen',
  'From': 'Von',
  'To': 'Bis',
  'Live register': 'Live-Register',
  'Compare': 'Vergleichen',
  'Print / export': 'Drucken / exportieren',
  'requirements': 'Anforderungen',
  'acceptance criteria': 'Abnahmekriterien',
  'milestones': 'Meilensteine',
  'identical, no changes': 'identisch, keine Änderungen',
  'identical': 'identisch',
  'Signature on record': 'Erfasste Unterschrift',
  'no ip recorded': 'keine IP erfasst',
  'no agent recorded': 'kein User-Agent erfasst',
  'Clause library': 'Klauselbibliothek',
  'shared across every engagement — an edit creates the NEXT version': 'über alle Aufträge geteilt — eine Bearbeitung erzeugt die NÄCHSTE Version',
  'Library': 'Bibliothek',
  'retired': 'stillgelegt',
  'New clause': 'Neue Klausel',
  'Revise {key}': '{key} revidieren',
  'Select a clause': 'Klausel auswählen',
  'Retire': 'Stilllegen',
  'Restore': 'Wiederherstellen',
  'Saving creates the NEXT version. Signed SOWs keep the version they printed — nothing already agreed changes retroactively.':
    'Speichern erzeugt die NÄCHSTE Version. Unterzeichnete SOWs behalten die gedruckte Version — nichts bereits Vereinbartes ändert sich rückwirkend.',
  'Key': 'Schlüssel',
  'e.g. subcontracting': 'z. B. subcontracting',
  'Body — markdown: paragraphs, **bold**, - bullets': 'Text — Markdown: Absätze, **fett**, - Aufzählungen',
  'Create clause': 'Klausel erstellen',
  'Save as new version': 'Als neue Version speichern',
  'Usage': 'Verwendung',
  'SOW version(s) cite this clause': 'SOW-Version(en) zitieren diese Klausel',
  'on a superseded version': 'auf einer abgelösten Version',
  'of those are SIGNED and keep the old wording': 'davon sind UNTERZEICHNET und behalten den alten Wortlaut',
  'Engagement': 'Auftrag',
  'Version': 'Version',
  'Clause': 'Klausel',
  'stale': 'veraltet',
  'Pick a clause to read it, revise it or see which SOW versions cite it.': 'Wählen Sie eine Klausel, um sie zu lesen, zu revidieren oder zu sehen, welche SOW-Versionen sie zitieren.',

  // ── the change-request panel ───────────────────────────────────────────────
  'visible to client': 'für Kunden sichtbar',
  'What was asked for': 'Was verlangt wurde',
  'origin not recorded': 'Herkunft nicht erfasst',
  'raised': 'gestellt',
  'Nothing was written down. A change request with no description is a title and a price.': 'Nichts wurde festgehalten. Ein Änderungsantrag ohne Beschreibung ist ein Titel und ein Preis.',
  'Impact': 'Auswirkung',
  'Edit impact': 'Auswirkung bearbeiten',
  'Cost ({currency})': 'Kosten ({currency})',
  'Schedule (days)': 'Zeitplan (Tage)',
  'Risk and dependencies': 'Risiko und Abhängigkeiten',
  'What this change puts at risk, and what it depends on.': 'Was diese Änderung gefährdet und wovon sie abhängt.',
  'Goodwill — absorbed at no charge, still on the record': 'Kulanz — kostenlos übernommen, bleibt aber aktenkundig',
  'Save impact': 'Auswirkung speichern',
  'Cost': 'Kosten',
  'not costed': 'nicht beziffert',
  'Schedule': 'Zeitplan',
  'day(s)': 'Tag(e)',
  'no schedule impact recorded': 'keine Auswirkung auf den Zeitplan erfasst',
  'Uncosted.': 'Unbeziffert.',
  'Submitting publishes it to the portal, where the sponsor is asked to approve a number nobody has written down.':
    'Das Einreichen veröffentlicht ihn im Portal, wo der Sponsor gebeten wird, eine Zahl zu genehmigen, die niemand aufgeschrieben hat.',
  'It was decided without one.': 'Er wurde ohne eine entschieden.',
  'Decision': 'Entscheidung',
  'by {name} (client)': 'von {name} (Kunde)',
  'recorded by {name}': 'erfasst von {name}',
  'A decided change request is part of the record; its impact is frozen with the decision.': 'Ein entschiedener Änderungsantrag ist Teil der Akte; seine Auswirkung ist mit der Entscheidung eingefroren.',
  'Register revisions this produced': 'Daraus entstandene Registerrevisionen',
  'carries': 'trägt',
  ', which is what let it through the baseline lock': ', was sie durch die Baseline-Sperre liess',
  'Notes': 'Notizen',
  'system': 'System',
  'Submit to client': 'Beim Kunden einreichen',
  'Approve and apply': 'Genehmigen und anwenden',
  'Defer': 'Zurückstellen',
  'Reject': 'Ablehnen',
  'Withdraw': 'Zurückziehen',
  'Delete this change request — refused once it has revised the register': 'Diesen Änderungsantrag löschen — verweigert, sobald er das Register revidiert hat',
  'Apply further revisions': 'Weitere Revisionen anwenden',

  // ── the issue modal ────────────────────────────────────────────────────────
  'Issue version {n}': 'Version {n} ausstellen',
  'Checking what issuing would do…': 'Es wird geprüft, was das Ausstellen bewirken würde…',
  'Hash the client signs': 'Hash, den der Kunde unterzeichnet',
  'Draft preview hash': 'Hash der Entwurfsvorschau',
  'Different by design: the draft carries the “DRAFT — not for signature” marker and the operator-only lint notes; the issued document carries neither.':
    'Absichtlich verschieden: Der Entwurf trägt die Markierung «DRAFT — not for signature» und die nur für Operatoren bestimmten Linter-Hinweise; das ausgestellte Dokument trägt keines von beidem.',
  'The register has moved since this draft was rendered.': 'Das Register hat sich bewegt, seit dieser Entwurf gerendert wurde.',
  "Issuing re-reads it, so the client gets today's register — not what the preview shows. Re-save the draft first if you want to see exactly what goes out.":
    'Das Ausstellen liest es erneut, der Kunde erhält also das heutige Register — nicht das, was die Vorschau zeigt. Speichern Sie den Entwurf zuerst erneut, wenn Sie genau sehen wollen, was hinausgeht.',
  '{n} acceptance criterion/criteria in Annex A are not verifiable as written.': '{n} Abnahmekriterium/-kriterien in Anhang A sind so, wie sie geschrieben sind, nicht prüfbar.',
  'Once issued they are contractual, and nobody — not the client, not you — can prove they are met. These are the same rows the pre-flight above lists.':
    'Einmal ausgestellt sind sie vertraglich bindend, und niemand — weder der Kunde noch Sie — kann nachweisen, dass sie erfüllt sind. Es sind dieselben Zeilen, die die Vorprüfung oben auflistet.',
  'I understand these acceptance criteria will be': 'Ich verstehe, dass diese Abnahmekriterien',
  'contractual as written': 'wie geschrieben vertraglich bindend',
  ', and I am issuing them anyway. (Otherwise: fix them on the Register page, re-save the draft, and come back.)':
    ' werden, und stelle sie trotzdem aus. (Andernfalls: auf der Register-Seite korrigieren, den Entwurf erneut speichern und zurückkommen.)',
  'The document is published to the client portal for signature and a PDF is generated. Further edits need a new version.':
    'Das Dokument wird zur Unterschrift im Kundenportal veröffentlicht und ein PDF wird erzeugt. Weitere Änderungen brauchen eine neue Version.',
  'Issue and publish': 'Ausstellen und veröffentlichen',

  // ── submit-uncosted and delete confirms ────────────────────────────────────
  'Submit {ref} uncosted?': '{ref} unbeziffert einreichen?',
  'Submit uncosted': 'Unbeziffert einreichen',
  'has neither a cost nor a schedule impact. Submitting publishes it to the client portal, where the sponsor is asked to approve it — effectively agreeing to an unknown number.':
    'hat weder Kosten- noch Zeitplanauswirkung. Das Einreichen veröffentlicht ihn im Kundenportal, wo der Sponsor gebeten wird, ihn zu genehmigen — und damit faktisch einer unbekannten Zahl zuzustimmen.',
  'Close this, use': 'Schliessen Sie dies, beziffern Sie ihn über',
  'to cost it, then submit. Continue only if the impact is genuinely nil and you want that on the record.':
    'und reichen Sie ihn dann ein. Fahren Sie nur fort, wenn die Auswirkung tatsächlich null ist und Sie das aktenkundig haben wollen.',
  'Delete {name}?': '{name} löschen?',
  'Deleting…': 'Wird gelöscht…',
  'Delete permanently': 'Endgültig löschen',
  'Immediate and permanent — this is for something that should never have existed. If it was real and simply ended, retire it instead ({alternative}). The audit log keeps the deleted row.':
    'Sofort und endgültig — das ist für etwas, das nie hätte existieren sollen. War es echt und ist einfach zu Ende, legen Sie es stattdessen still ({alternative}). Der Prüfpfad behält die gelöschte Zeile.',
  'set the status to delivered or accepted': 'Status auf geliefert oder abgenommen setzen',
  'reject or withdraw it': 'ablehnen oder zurückziehen',
  'issue it and supersede it later': 'ausstellen und später ablösen',
  '{name} cannot be deleted': '{name} kann nicht gelöscht werden',

  // ── new SOW, sign, new CR, approve modals ──────────────────────────────────
  'New Statement of Work': 'Neue Leistungsbeschreibung (SOW)',
  'Statement of Work — {name}': 'Leistungsbeschreibung — {name}',
  'Version 1 is rendered from the live register immediately.': 'Version 1 wird sofort aus dem Live-Register gerendert.',
  'The {values} you accepted from the record ({keys}) go in with it.': 'Die {values}, die Sie aus der Akte übernommen haben ({keys}), gehen damit hinein.',
  '{n} value': '{n} Wert',
  '{n} values': '{n} Werte',
  'Create and render': 'Erstellen und rendern',
  'Record the signature on v{n}': 'Unterschrift auf v{n} erfassen',
  'This freezes the baseline.': 'Dies friert die Baseline ein.',
  'Recording the signature serialises the whole register as an immutable snapshot, hashes it, and flips every held requirement to':
    'Das Erfassen der Unterschrift serialisiert das gesamte Register als unveränderliche Momentaufnahme, hasht es und setzt jede übernommene Anforderung auf',
  'After this, scope moves only through a change request.': 'Danach bewegt sich der Umfang nur noch über einen Änderungsantrag.',
  'Document hash': 'Dokument-Hash',
  'Client signatory — typed name, exactly as signed': 'Unterzeichnende Person beim Kunden — getippter Name, genau wie unterschrieben',
  'Date signed (optional — defaults to now)': 'Unterschriftsdatum (optional — standardmässig jetzt)',
  'I confirm the named signatory has signed this exact document, and I understand that recording it freezes the requirements register as an immutable baseline.':
    'Ich bestätige, dass die genannte Person genau dieses Dokument unterzeichnet hat, und verstehe, dass das Erfassen das Anforderungsregister als unveränderliche Baseline einfriert.',
  'Record signature and baseline': 'Unterschrift erfassen und Baseline setzen',
  'What is being asked for, and why': 'Was verlangt wird, und warum',
  'Origin — who asked, when': 'Herkunft — wer hat wann gefragt',
  'Impact — cost': 'Auswirkung — Kosten',
  'Impact — days': 'Auswirkung — Tage',
  'Impact — risk': 'Auswirkung — Risiko',
  'Goodwill — absorbed at no charge, but recorded so the concession stays visible': 'Kulanz — kostenlos übernommen, aber erfasst, damit das Entgegenkommen sichtbar bleibt',
  'Create draft': 'Entwurf erstellen',
  'Approve {ref} — compose the register revisions': '{ref} genehmigen — Registerrevisionen zusammenstellen',
  'Approval and the revisions are applied in one transaction. Every revision carries': 'Genehmigung und Revisionen werden in einer Transaktion angewendet. Jede Revision trägt',
  ', which is what lets it through the baseline lock and what answers “why did this change?” later. Pick the requirement and its current wording appears — you are editing text you can read, not a truncated line in a dropdown.':
    ', was sie durch die Baseline-Sperre lässt und später die Frage «Warum hat sich das geändert?» beantwortet. Wählen Sie die Anforderung, und ihr aktueller Wortlaut erscheint — Sie bearbeiten Text, den Sie lesen können, nicht eine abgeschnittene Zeile in einer Auswahlliste.',
  'Revision action': 'Revisionsaktion',
  'Requirement': 'Anforderung',
  'Pick a requirement…': 'Anforderung wählen…',
  'Remove revision': 'Revision entfernen',
  'Current wording — this is what changes': 'Aktueller Wortlaut — das ist, was sich ändert',
  'Reason for removing it from scope': 'Grund, sie aus dem Umfang zu nehmen',
  'Descope reason': 'Grund für das Herausnehmen',
  'Statement': 'Aussage',
  'New requirement statement': 'Aussage der neuen Anforderung',
  'Edited statement (leave empty to keep the current wording)': 'Bearbeitete Aussage (leer lassen, um den aktuellen Wortlaut zu behalten)',
  'Rationale': 'Begründung',
  'Type': 'Typ',
  'Priority': 'Priorität',
  'Category': 'Kategorie',
  'Add a revision': 'Revision hinzufügen',
  'Record as goodwill — absorbed at no charge, still on the record': 'Als Kulanz erfassen — kostenlos übernommen, bleibt aber aktenkundig',
  'Draft an addendum SOW version from the register after applying': 'Nach dem Anwenden eine Nachtrags-SOW-Version aus dem Register entwerfen',
  'Approve and apply {n} revision(s)': 'Genehmigen und {n} Revision(en) anwenden',
}, 'ContractingPage');
